import { Button, IconButton, useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type AgentMemoryGroup,
  clearAgentMemoryMutation,
  clearAllAgentMemoryMutation,
  deleteAgentMemoryMutation,
  errorMessage,
  listAgentMemoriesOptions,
  listAgentMemoriesQueryKey,
} from "@/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { QuerySection } from "@/components/section";
import { Text } from "@/components/Text";

/** "preferred_email_length" -> "Preferred email length". */
const label = (key: string) => {
  const words = key.replaceAll("_", " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** Every note the user's agents keep about them, by agent, each deletable. */
export function MemoryList() {
  const { t } = useTranslation("settings", { keyPrefix: "memory" });
  const toast = useToast();
  const queryClient = useQueryClient();
  const [confirmAll, setConfirmAll] = useState(false);
  const groups = useQuery({ ...listAgentMemoriesOptions(), select: (r) => r.data.groups });
  const refresh = () => queryClient.invalidateQueries({ queryKey: listAgentMemoriesQueryKey() });
  const onError = (error: unknown) => toast.error(errorMessage(error, t("deleteFailed")));
  const removeOne = useMutation({ ...deleteAgentMemoryMutation(), onSettled: refresh, onError });
  const clearAgent = useMutation({ ...clearAgentMemoryMutation(), onSettled: refresh, onError });
  const clearAll = useMutation({
    ...clearAllAgentMemoryMutation(),
    onSettled: refresh,
    onSuccess: () => toast.success(t("cleared")),
    onError,
  });

  const agentName = (g: AgentMemoryGroup) => g.agentName ?? t("defaultAgent");
  return (
    <div className="flex flex-col gap-3">
      <QuerySection query={groups} isEmpty={(list) => list.length === 0} empty={{ hint: t("empty") }} loadingRows={2}>
        {(list) => (
          <>
            {list.map((group) => (
              <section key={group.agentUuid ?? "default"} aria-label={agentName(group)} className="flex flex-col gap-1">
                <div className="flex items-center justify-between gap-2">
                  <Text variant="body-sm" weight="semibold">
                    {agentName(group)}
                  </Text>
                  <Button
                    variant="text"
                    size="sm"
                    onClick={() => clearAgent.mutate({ path: { agent_uuid: group.agentUuid ?? "default" } })}
                  >
                    {t("forgetAgent")}
                  </Button>
                </div>
                <ul className="flex flex-col divide-y divide-border-subtle">
                  {group.entries.map((entry) => (
                    <li key={entry.uuid} className="flex items-start gap-3 py-2">
                      <div className="min-w-0 flex-1">
                        <Text variant="caption" tone="tertiary">
                          {label(entry.key)}
                        </Text>
                        <Text variant="body-sm" className="break-words">
                          {entry.value}
                        </Text>
                      </div>
                      <IconButton
                        icon={<Trash2 />}
                        variant="ghost"
                        size="sm"
                        aria-label={t("forget", { note: label(entry.key) })}
                        onClick={() => removeOne.mutate({ path: { entry_uuid: entry.uuid } })}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            ))}
            <div>
              <Button variant="secondary" size="sm" onClick={() => setConfirmAll(true)}>
                {t("forgetAll")}
              </Button>
            </div>
          </>
        )}
      </QuerySection>
      <ConfirmDialog
        open={confirmAll}
        onClose={() => setConfirmAll(false)}
        title={t("confirmTitle")}
        body={t("confirmBody")}
        confirmLabel={t("forgetAll")}
        onConfirm={() => {
          clearAll.mutate({});
          setConfirmAll(false);
        }}
      />
    </div>
  );
}
